import { Col, Form, Modal, ModalProps, Row } from 'antd';

import { useEffect, useState } from 'react';

import { ColumnsType } from 'antd/lib/table';

import { useIntl } from 'react-intl';

import { Key } from 'antd/es/table/interface';

import { flatInfiniteData } from '@/utils/request/flatInfiniteData';

import {
  useAccountReceivableDeleteMultipleInstallments,
  useAccountReceivableList,
  useAccountReceivableReverseMultipleInstallments,
} from '@/core/domain/financial/AccountReceivable/AccountReceivable.hooks';
import {
  AccountReceivable,
  AccountReceivableListRequest,
  AccountReceivableOrderColumn,
  AccountReceivableSituation,
} from '@/core/domain/financial/AccountReceivable/AccountReceivable.types';
import {
  AppPageHeader,
  FloatingFormItem,
  InfiniteTable,
  PlanAccountsSelect,
} from '@/components/_common';
import {
  PlanAccountDestination,
  PlanAccountHierarchy,
} from '@/core/domain/enterprise/PlanAccounts/PlanAccounts.types';
import { useDefaultEmptyFieldError } from '@/hooks';
import { COLUMNS_WIDTH, tableSorterOrder } from '@/utils/table';
import moment from 'moment';
import { quantityFormatter } from '@/utils/number';
import { SorterResult } from 'antd/lib/table/interface';
import { DateTimeFormat } from '@/utils/moment';
import AccountReceivableListExtraFilters from './AccountReceivableListExtraFilters';

interface SelectedAccountReceivablesProps extends Omit<ModalProps, 'onCancel'> {
  selectedInstallments?: { installmentId: number[]; planAccountId: number };
  onCancel: () => void;
  btnType?: 'reverse' | 'delete';
}

export const AccountReceivableReverseMultipleInstallmentsModal: React.FC<
  SelectedAccountReceivablesProps
> = ({ btnType, open, onCancel, selectedInstallments }) => {
  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const intl = useIntl();
  const [form] = Form.useForm();

  const [queryParams, setQueryParams] = useState<AccountReceivableListRequest>({
    order: 'DESC',
    situation: AccountReceivableSituation.settled,
  });

  const [selectedInstallmentsKeys, setSelectedInstallmentsKeys] = useState<
    Key[]
  >([]);

  const { data, isFetching, hasNextPage, fetchNextPage } =
    useAccountReceivableList(
      {
        ...queryParams,
        situation:
          btnType === 'delete'
            ? AccountReceivableSituation.open
            : AccountReceivableSituation.settled,
      },
      open,
    );

  const accountReceivables = flatInfiniteData(data);

  const reverseMutation = useAccountReceivableReverseMultipleInstallments();
  const deleteMutation = useAccountReceivableDeleteMultipleInstallments();

  useEffect(() => {
    if (open && selectedInstallments) {
      setSelectedInstallmentsKeys({ ...selectedInstallments?.installmentId });
    }
  }, [open]);

  const handleOk = async () => {
    const values = await form.validateFields();

    if (btnType === 'reverse') {
      await reverseMutation.mutateAsync({
        installmentReceivableId: selectedInstallmentsKeys,
        planAccountId: values.planAccountId,
        reversalDate: moment().format(DateTimeFormat),
      });

      handleCancel();
    }

    if (btnType === 'delete') {
      await deleteMutation.mutateAsync({
        installmentIds: selectedInstallmentsKeys,
      });

      handleCancel();
    }
  };

  const handleCancel = () => {
    setQueryParams({
      order: 'DESC',
      situation: AccountReceivableSituation.settled,
    });

    setSelectedInstallmentsKeys([]);

    onCancel();
  };

  const columns: ColumnsType<AccountReceivable> = [
    {
      width: COLUMNS_WIDTH.price,
      dataIndex: 'code',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.code',
      }),
      sorter: true,
      key: AccountReceivableOrderColumn.code,
    },
    {
      dataIndex: 'companyName',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.companies',
      }),
      width: 220,
      key: AccountReceivableOrderColumn.company,
      sorter: true,
      ellipsis: true,
    },
    {
      width: 220,
      dataIndex: 'debtor',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.debtor',
      }),
      sorter: true,
      ellipsis: true,
      key: AccountReceivableOrderColumn.debtor,
    },
    {
      width: COLUMNS_WIDTH.action2,
      dataIndex: 'description',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.description',
      }),
      sorter: true,
      ellipsis: true,
      key: AccountReceivableOrderColumn.description,
    },
    {
      width: 100,
      dataIndex: 'emissionDate',
      title: 'Dt. emissão',
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      sorter: true,
      key: AccountReceivableOrderColumn.emissionDate,
    },
    {
      width: 100,
      dataIndex: 'dueDate',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.dueDate',
      }),
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      sorter: true,
      key: AccountReceivableOrderColumn.dueDate,
    },
    {
      width: 110,
      dataIndex: 'payDate',
      title: 'Dt. pagamento',
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      sorter: true,
      key: AccountReceivableOrderColumn.payDate,
    },
    {
      width: COLUMNS_WIDTH.price,
      dataIndex: 'installmentValue',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.value',
      }),
      sorter: true,
      align: 'right',
      key: AccountReceivableOrderColumn.value,
      render: (_, item) =>
        `R$ ${quantityFormatter(
          item.installmentValue + (item.discountIncrease ?? 0),
        )}`,
    },
  ];

  function handleOrderAccountReceivable(
    sorter: SorterResult<AccountReceivable>,
  ) {
    if (sorter && sorter?.columnKey) {
      setQueryParams((prev) => {
        if (sorter && sorter?.columnKey) {
          const order = sorter.order
            ? tableSorterOrder[sorter.order]
            : undefined;

          if (
            prev.orderColumns ===
            (sorter.columnKey as AccountReceivableOrderColumn)
          ) {
            return {
              ...prev,
              order,
            };
          }

          return {
            ...prev,
            order: tableSorterOrder.ascend,
            orderColumns: sorter.columnKey as AccountReceivableOrderColumn,
          };
        }

        return prev;
      });
    }
  }

  const handleInstallmentSelect = (selectedRowKeys: Key[]) => {
    setSelectedInstallmentsKeys(selectedRowKeys);
  };

  return (
    <Modal
      title={`${
        btnType === 'delete' ? 'Exclusão' : 'Estorno'
      } de múltiplas parcelas`}
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      width={1400}
      okText={btnType === 'delete' ? 'Excluir' : 'Estornar'}
      okButtonProps={{ disabled: !selectedInstallmentsKeys.length }}
      confirmLoading={reverseMutation.isLoading || deleteMutation.isLoading}
    >
      <Row gutter={16}>
        <Col flex="1">
          <div style={{ width: '100%' }}>
            {btnType === 'reverse' && (
              <div style={{ marginBottom: '24px' }}>
                <Form form={form}>
                  <FloatingFormItem
                    label="Selecione o plano de contas"
                    name="planAccountId"
                    rules={[defaultEmptyFieldError]}
                  >
                    <PlanAccountsSelect
                      allowClear
                      queryFilter={{
                        active: true,
                        hierarchy: PlanAccountHierarchy.Analitical,
                        destination: PlanAccountDestination.Expense,
                      }}
                    />
                  </FloatingFormItem>
                </Form>
              </div>
            )}

            <AppPageHeader
              onSearch={(value) =>
                setQueryParams((prev) => ({ ...prev, search: value }))
              }
              searchValue={queryParams.search}
              extraFilters={
                <AccountReceivableListExtraFilters
                  setQueryParams={setQueryParams}
                  queryParams={queryParams}
                />
              }
            />

            <InfiniteTable<AccountReceivable>
              columns={columns}
              dataSource={accountReceivables}
              loading={isFetching}
              rowKey={(row) => row.id}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              rowSelection={{
                preserveSelectedRowKeys: true,
                selectedRowKeys: selectedInstallmentsKeys,
                onChange: handleInstallmentSelect,
              }}
              onChange={(_p, _f, sorter) => {
                if (!Array.isArray(sorter)) {
                  handleOrderAccountReceivable(sorter);
                }
              }}
            />
          </div>
        </Col>
      </Row>
    </Modal>
  );
};
